<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Page Templates
      </h2>
      <button
        @click="showCreateForm = true"
        class="btn btn-primary btn-sm"
      >
        Create Template
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
    </div>

    <div v-else-if="templates.length === 0" class="text-center py-12">
      <div class="text-gray-400 dark:text-gray-500 mb-4">
        <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      </div>
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">No templates yet</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Create a template from an existing page to get started.
      </p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="template in templates"
        :key="template.id"
        @click="$emit('select-template', template)"
        class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
      >
        <div class="aspect-video bg-gray-100 dark:bg-gray-700 relative">
          <img
            v-if="template.reference_screenshot_url"
            :src="template.reference_screenshot_url"
            :alt="template.name"
            class="w-full h-full object-cover object-top"
          />
          <div v-else class="flex items-center justify-center h-full text-gray-400">
            <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </div>

          <div
            v-if="buildProgress[template.id]"
            class="absolute inset-0 bg-black/50 flex items-center justify-center"
          >
            <div class="text-center text-white">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
              <span class="text-xs">{{ buildProgress[template.id].message }}</span>
            </div>
          </div>
        </div>

        <div class="p-4">
          <div class="flex items-start justify-between">
            <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ template.name }}
            </h3>
            <span :class="statusClass(template.status)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ml-2 flex-shrink-0">
              {{ template.status }}
            </span>
          </div>
          <div class="mt-2 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span class="capitalize">{{ template.page_type?.replace('_', ' ') }}</span>
            <span>&middot;</span>
            <span>{{ template.page_builds_count || 0 }} builds</span>
            <span>&middot;</span>
            <span>v{{ template.version }}</span>
          </div>
        </div>
      </div>
    </div>

    <UiSlidePanel
      v-if="showCreateForm"
      title="Create Template from Page"
      @close="showCreateForm = false"
    >
      <form @submit.prevent="handleCreate">
        <UiInput
          v-model="form.name"
          label="Template Name"
          placeholder="e.g., Location Page Template"
          required
        />
        <UiSelect
          v-model="form.page_type"
          label="Page Type"
          :options="pageTypeOptions"
          class="mt-4"
        />
        <UiSelect
          v-model="form.source_website_page_id"
          label="Reference Page"
          :options="pageOptions"
          class="mt-4"
          required
        />
        <div class="mt-6 flex justify-end gap-3">
          <button type="button" @click="showCreateForm = false" class="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" :disabled="creating" class="btn btn-cta btn-sm">
            {{ creating ? 'Creating...' : 'Create Template' }}
          </button>
        </div>
      </form>
    </UiSlidePanel>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { getPageTemplates, createPageTemplate } from '~/javascript/api/page_templates'
import { getWebsitePages } from '~/javascript/api/website_pages'
import consumer from '~/channels/consumer'

const props = defineProps({
  websiteId: { type: String, required: true }
})

const emit = defineEmits(['select-template'])

const templates = ref([])
const loading = ref(true)
const showCreateForm = ref(false)
const creating = ref(false)
const pages = ref([])
const buildProgress = ref({})
const subscriptions = ref({})

const form = ref({
  name: '',
  page_type: 'location_page',
  source_website_page_id: ''
})

const pageTypeOptions = [
  { value: 'page', label: 'Page' },
  { value: 'location_page', label: 'Location Page' },
  { value: 'service_page', label: 'Service Page' },
  { value: 'service_location_page', label: 'Service + Location Page' }
]

const pageOptions = ref([])

const statusClass = (status) => {
  const classes = {
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    archived: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
  }
  return classes[status] || classes.draft
}

const fetchTemplates = async () => {
  loading.value = true
  try {
    const { data } = await getPageTemplates(props.websiteId)
    templates.value = data
    templates.value
      .filter(t => t.status === 'draft')
      .forEach(t => subscribeToTemplate(t.id))
  } finally {
    loading.value = false
  }
}

const fetchPages = async () => {
  try {
    const { data } = await getWebsitePages(props.websiteId)
    pageOptions.value = data.map(p => ({ value: p.id, label: `${p.slug} — ${p.meta_title || p.slug}` }))
  } catch (e) {
    console.error('Failed to load pages', e)
  }
}

const handleCreate = async () => {
  creating.value = true
  try {
    const { data } = await createPageTemplate(props.websiteId, { page_template: form.value })
    templates.value.unshift(data)
    subscribeToTemplate(data.id)
    showCreateForm.value = false
    form.value = { name: '', page_type: 'location_page', source_website_page_id: '' }
  } catch (e) {
    console.error('Failed to create template', e)
  } finally {
    creating.value = false
  }
}

const subscribeToTemplate = (templateId) => {
  if (subscriptions.value[templateId]) return

  subscriptions.value[templateId] = consumer.subscriptions.create(
    { channel: 'PageTemplateChannel', template_id: templateId },
    {
      received(data) {
        if (data.event === 'build_progress') {
          buildProgress.value[templateId] = data
        } else if (data.event === 'build_completed') {
          delete buildProgress.value[templateId]
          fetchTemplates()
        } else if (data.event === 'build_failed') {
          delete buildProgress.value[templateId]
          fetchTemplates()
        }
      }
    }
  )
}

onMounted(() => {
  fetchTemplates()
  fetchPages()
})

onUnmounted(() => {
  Object.values(subscriptions.value).forEach(sub => sub.unsubscribe())
})
</script>
